import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
  getDocumentBriefing,
  createAnalysisJob,
  listAnalysisJobs,
  listMatterAnalysisJobs,
  getAnalysisJobStatus,
  cancelAnalysisJob as cancelAnalysisJobRequest,
  getAnalysisJobReport,
  getDocumentContextAnalysis,
  refreshDocumentContextAnalysis,
} from "../service/analysis";

/**
 * Status values that mean "the backend job is finished, polling should
 * stop." Centralized here so the slice, the polling hook, and the UI
 * agree on the boundary. Anything not in this set is treated as
 * still-running for polling purposes.
 */
export const TERMINAL_JOB_STATUSES = Object.freeze([
  "completed",
  "completed_with_errors",
  "failed",
  "cancelled",
]);

export const isTerminalJobStatus = (statusValue) =>
  TERMINAL_JOB_STATUSES.includes(statusValue);

/**
 * Statuses that mean "this Brief is active" — either the worker is
 * executing (`running`) or the job is waiting its turn behind the
 * user's concurrent-Brief cap (`queued`, BE PR #1898) and will be
 * promoted to running automatically when a slot frees.
 *
 * Used everywhere the question is "is a Brief in flight for this
 * matter/doc?" — disabling re-trigger controls, picking the job the
 * pane renders, and keeping the poll alive so the UI flips to
 * running the moment the backend promotes the job. Sites that
 * specifically mean "the worker is executing right now" (the elapsed-
 * time clock, the spinner affordances) keep checking
 * `status === "running"` directly.
 */
export const isActiveBriefStatus = (statusValue) =>
  statusValue === "running" || statusValue === "queued";

/**
 * Terminal context-analysis statuses. Distinct from the deep
 * AnalysisJob FSM above because Context Analysis is a simpler 4-state
 * pipeline (`pending → running → completed | failed`) — no
 * `completed_with_errors`, no `cancelled`. Centralized so the polling
 * hook and the slice agree on when to stop polling.
 *
 * `not_started` (BE's shape when no row exists) is deliberately absent:
 * it is not terminal, because a producer run triggered elsewhere should
 * still be picked up by polling.
 */
export const TERMINAL_CONTEXT_ANALYSIS_STATUSES = Object.freeze([
  "completed",
  "failed",
]);

export const isTerminalContextAnalysisStatus = (statusValue) =>
  TERMINAL_CONTEXT_ANALYSIS_STATUSES.includes(statusValue);

/**
 * Statuses that mean "a producer run is actually in flight".
 *
 * Non-terminal is NOT the same as in-flight: `not_started` is
 * non-terminal (keep polling) but nothing is running, so the panel must
 * offer its start control rather than disable it as "already running".
 * Conflating the two is what made the panel a dead end.
 */
export const IN_FLIGHT_CONTEXT_ANALYSIS_STATUSES = Object.freeze([
  "pending",
  "running",
]);

export const isInFlightContextAnalysisStatus = (statusValue) =>
  IN_FLIGHT_CONTEXT_ANALYSIS_STATUSES.includes(statusValue);

/**
 * AI panel state.
 *
 * Per-document briefings and analysis jobs are stored in id-keyed maps so
 * switching between open documents is instant (no refetch on tab switch),
 * and so a long-running analysis on doc A doesn't block UI for doc B.
 *
 * Phase 1 covers: briefing fetch (with `empty` 404 state), job-list
 * hydration (so the panel can detect a running job at mount time), and
 * the trigger action (which optimistically appends a synthetic running
 * job so the UI transitions empty → running). Real polling, per-job
 * status state, and the full markdown report land in Phase 3 / Phase 4.
 */

const initialState = {
  // Map<documentId, { status, data, error }>
  // status: "idle" | "loading" | "ready" | "empty" | "failed"
  // data:   DocumentBriefing | null
  briefings: {},

  // Map<documentId, { status, jobs, error }>
  // jobs: AnalysisJob[] newest-first
  jobsByDocument: {},

  // Map<matterId, { status, jobs, error }>
  //
  // Mirror of `jobsByDocument` for matter-scoped fetches. Populated
  // in lockstep with `jobsByDocument` whenever a `fetchAnalysisJobs`
  // call carries a `matterId` (the BE returns matter-wide jobs in
  // that case — same payload, same shape — so the two caches are
  // always equivalent for that response, just keyed differently).
  //
  // The reason this exists: cross-document navigation triggered by
  // a Brief citation click switches the active document but stays
  // inside the same matter. Without a matter-keyed cache, the
  // citation jump's destination document has no `jobsByDocument[id]`
  // entry yet, so AnalysisTab's idle/loading guard kicks in and
  // shows a full-pane skeleton — the user perceives this as "the
  // Brief disappeared." With this cache, the AnalysisTab fallback
  // selector reads the matter-wide cache and surfaces the same Brief
  // immediately, then the per-document fetch backfills behind the
  // scenes.
  jobsByMatter: {},

  // Phase 4: full reports keyed by jobId, fetched lazily when
  // the Analysis tab opens on a terminal job. Separate from
  // `jobsByDocument` because the report payload is much larger
  // (full markdown + per-condition metadata + strategic
  // narrative) and we don't want it to bloat the 5s poll cache
  // or the matter-table running-jobs derivation.
  // Map<jobId, { status, data, error }>
  // status: "idle" | "loading" | "ready" | "failed"
  // data:   AnalysisJobReport | null
  reportsByJob: {},

  // Phase 5: per-document context analysis ("fit into matter"
  // narrative + recommended actions, computed eager-on-arrival
  // by the BE producer). Keyed on documentId because:
  //   - The artifact is per-document (one row per Document via
  //     OneToOneField on the BE).
  //   - The Inbox right-rail and the matter-workspace right-rail
  //     both consume the same data, so a single cache slot keeps
  //     the panes consistent if the user clicks "Open in matter"
  //     mid-poll.
  //   - The BE FSM has its own statuses (pending/running/completed/
  //     failed) distinct from briefings (idle/loading/ready/empty/
  //     failed) and AnalysisJobs (running/completed/failed/cancelled),
  //     so a separate slot avoids enum collisions on the same field.
  // Map<documentId, { status, data, error, lastFetchAt }>
  // status: "idle" | "loading" | "ready" | "failed"
  //   - "idle" — no fetch dispatched yet
  //   - "loading" — fetch in flight
  //   - "ready" — last fetch fulfilled (data may still be
  //               BE-status="pending" or "running"; polling
  //               continues based on data.status, not slice.status)
  //   - "failed" — last fetch rejected
  // data:   ContextAnalysis | null
  //   { status, content_md, recommended_actions, computed_at,
  //     model_version, error_message }
  contextAnalysisByDocument: {},
};

// ---------------------------------------------------------------------------
// Briefing
// ---------------------------------------------------------------------------

export const fetchDocumentBriefing = createAsyncThunk(
  "ai/fetchDocumentBriefing",
  async (documentId, { rejectWithValue }) => {
    if (!documentId) return rejectWithValue({ documentId, message: "missing documentId" });
    try {
      const response = await getDocumentBriefing(documentId);
      // The service layer downgrades 404 to a non-thrown response so we can
      // distinguish "no briefing yet" (empty state) from a real error.
      if (!response || response.status === 404) {
        return { documentId, briefing: null, empty: true };
      }
      return { documentId, briefing: response.data, empty: false };
    } catch (err) {
      return rejectWithValue({
        documentId,
        message: err?.response?.data?.detail || err?.message || "Failed to load briefing",
        status: err?.response?.status,
      });
    }
  }
);

// ---------------------------------------------------------------------------
// Analysis jobs
// ---------------------------------------------------------------------------

/**
 * On panel mount we ask the backend "any jobs for this claim file?" so we
 * can resume a still-running analysis (or restore the last completed one)
 * regardless of which document inside the matter is currently open.
 *
 * Args (back-compat — accepts either shape):
 *   - `fetchAnalysisJobs(documentId)`            — legacy doc-scoped fetch
 *   - `fetchAnalysisJobs({ documentId, matterId })` — matter-scoped fetch
 *     when `matterId` is present (preferred — mirrors prototype's
 *     `GET /api/clients/{client_id}/analysis-jobs`).
 *
 * The result is still cached under `state.jobsByDocument[documentId]`
 * so existing selectors (`selectActiveJobForDocument`,
 * `selectIsAnalysisRunningForDocument`, etc.) keep working unchanged.
 * The list comes from a matter-wide query under the hood, so a sibling
 * doc in the same matter that fires its own fetch sees the same jobs.
 */
export const fetchAnalysisJobs = createAsyncThunk(
  "ai/fetchAnalysisJobs",
  async (arg, { rejectWithValue }) => {
    // Accept either a bare documentId (legacy) or { documentId, matterId }.
    const { documentId, matterId } =
      arg && typeof arg === "object" ? arg : { documentId: arg, matterId: null };

    // Matter-scoped fetches don't need a documentId — the AI pane can
    // sit at the matter root (no document open) and still hydrate the
    // matter-wide Brief. A documentId alone is the legacy doc-scoped
    // path; both missing is a caller bug.
    if (!documentId && !matterId) {
      return rejectWithValue({ documentId, message: "missing documentId" });
    }

    try {
      const response = matterId
        ? await listMatterAnalysisJobs(matterId)
        : await listAnalysisJobs(documentId);
      const jobs = Array.isArray(response?.data) ? response.data : [];
      return { documentId, matterId, jobs };
    } catch (err) {
      return rejectWithValue({
        documentId,
        matterId,
        message: err?.response?.data?.detail || err?.message || "Failed to load analysis jobs",
      });
    }
  }
);

export const triggerAnalysisJob = createAsyncThunk(
  "ai/triggerAnalysisJob",
  // `matterId` rides along so the fulfilled reducer can prepend the
  // optimistic synthetic job to the matter cache too — the AI pane
  // renders matter-first, so a synthetic living only in the per-doc
  // slot would leave the pane blind to the run it just started.
  // `queue` (BE PR #1898): re-dispatch with `queue: true` after a
  // queueable 429 to create the job as status="queued" instead of
  // dead-ending — the backend starts it when the running Brief
  // finishes.
  async (
    { documentId, claimedOnly = false, matterId = null, queue = false } = {},
    { rejectWithValue }
  ) => {
    if (!documentId) return rejectWithValue({ documentId, message: "missing documentId" });
    try {
      const response = await createAnalysisJob(documentId, { claimedOnly, queue });
      const job = response?.data || {};
      return { documentId, matterId, job };
    } catch (err) {
      // A capacity 429 is not a dead end anymore: the body carries the
      // running Brief's progress + `queueable: true` so AnalysisTab can
      // offer "Queue this Brief" instead of a flat red error. Carry the
      // whole body through rejectWithValue; `message` keeps its old
      // shape so existing error rendering is untouched.
      const body = err?.response?.data || {};
      return rejectWithValue({
        documentId,
        message: body?.detail || err?.message || "Failed to start analysis",
        status: err?.response?.status,
        detail: body?.detail,
        queueable: Boolean(body?.queueable),
        running_brief: body?.running_brief || null,
      });
    }
  }
);

/**
 * Lightweight 5-second poll. Returns the job's status, progress
 * counters, and `conditions[]` (per-condition status only — no
 * analysis text, that lives in Phase 4's report endpoint).
 *
 * Carries `documentId` alongside the response so the reducer can
 * patch the right slot in `state.jobsByDocument` without scanning
 * every doc. The patch is "merge" semantics: existing fields on the
 * cached job (notably the synthetic `created_at` from the
 * optimistic trigger insert) survive if the response omits them.
 */
export const fetchAnalysisJobStatus = createAsyncThunk(
  "ai/fetchAnalysisJobStatus",
  async ({ documentId, jobId } = {}, { rejectWithValue }) => {
    if (!documentId || !jobId) {
      return rejectWithValue({
        documentId,
        jobId,
        message: "missing documentId or jobId",
      });
    }
    try {
      const response = await getAnalysisJobStatus(jobId);
      return { documentId, jobId, job: response?.data || {} };
    } catch (err) {
      return rejectWithValue({
        documentId,
        jobId,
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to poll analysis job status",
        status: err?.response?.status,
      });
    }
  }
);

/**
 * Cancel a running Brief job — the real "Cancel" button (2026-08-18).
 *
 * Before this existed, an Anthropic capacity outage leaving a job
 * stuck "analyzing" for 30+ minutes had no user-facing way to stop —
 * only a hand-written database fix. The backend hard-stops the
 * in-flight worker (revoke + terminate), so this resolves fast; the
 * fulfilled handler patches the job in place immediately rather than
 * waiting for the next 5s poll tick, matching `fetchAnalysisJobStatus`.
 */
export const cancelAnalysisJob = createAsyncThunk(
  "ai/cancelAnalysisJob",
  async ({ documentId, jobId } = {}, { rejectWithValue }) => {
    if (!documentId || !jobId) {
      return rejectWithValue({
        documentId,
        jobId,
        message: "missing documentId or jobId",
      });
    }
    try {
      const response = await cancelAnalysisJobRequest(jobId);
      return { documentId, jobId, job: response?.data || {} };
    } catch (err) {
      return rejectWithValue({
        documentId,
        jobId,
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to cancel the Brief",
        status: err?.response?.status,
      });
    }
  }
);

/**
 * Fetch the full attorney-analysis report for a terminal
 * (completed / completed_with_errors) job.
 *
 * Lazy by design: the AnalysisTab dispatches this once when it
 * mounts on a terminal job (or transitions running → terminal),
 * and the slice keys the cache on `jobId` so re-opening the
 * panel on the same job is a no-op. Re-runs of the same doc
 * produce a *new* `jobId` (the optimistic synthetic id from
 * trigger), so the cache never serves stale content for a new
 * analysis.
 *
 * The report payload includes `discovery_data` (which holds
 * `strategic_narrative`) and `condition_analyses[]` (each with
 * full markdown `analysis_text` + `condition_metadata`).
 */
export const fetchAnalysisJobReport = createAsyncThunk(
  "ai/fetchAnalysisJobReport",
  async (jobId, { rejectWithValue }) => {
    if (!jobId) {
      return rejectWithValue({
        jobId,
        message: "missing jobId",
      });
    }
    try {
      const response = await getAnalysisJobReport(jobId);
      return { jobId, report: response?.data || {} };
    } catch (err) {
      return rejectWithValue({
        jobId,
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load analysis report",
        status: err?.response?.status,
      });
    }
  }
);

// ---------------------------------------------------------------------------
// Context Analysis (Phase 5)
// ---------------------------------------------------------------------------

/**
 * Fetch the per-document context analysis row.
 *
 * The BE always returns 200 (even when no row exists yet — in that case
 * `status="pending"`, `content_md=""`, `computed_at=null`). The slice
 * doesn't need a 404 special case; it just stores whatever came back.
 *
 * The polling hook (`usePollContextAnalysis`) keeps calling this every
 * 5s while the cached `data.status` is non-terminal (`pending` or
 * `running`, including the null-as-pending case), and stops on
 * `completed` / `failed`.
 */
export const fetchContextAnalysis = createAsyncThunk(
  "ai/fetchContextAnalysis",
  async (documentId, { rejectWithValue }) => {
    if (!documentId) {
      return rejectWithValue({
        documentId,
        message: "missing documentId",
      });
    }
    try {
      const response = await getDocumentContextAnalysis(documentId);
      return { documentId, contextAnalysis: response?.data || null };
    } catch (err) {
      return rejectWithValue({
        documentId,
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to load context analysis",
        status: err?.response?.status,
      });
    }
  }
);

/**
 * POST refresh — re-enqueues a context analysis run. Returns 202 with
 * the row reset to `status="pending"`, which the FE optimistically
 * reflects in the slice so the polling hook resumes immediately
 * without waiting for the next GET.
 *
 * Phase 5b consumer (the refresh button in the right-rail action
 * footer); thunk lives here so the AISlice owns the full context-
 * analysis lifecycle.
 */
export const refreshContextAnalysis = createAsyncThunk(
  "ai/refreshContextAnalysis",
  async (documentId, { rejectWithValue }) => {
    if (!documentId) {
      return rejectWithValue({
        documentId,
        message: "missing documentId",
      });
    }
    try {
      const response = await refreshDocumentContextAnalysis(documentId);
      return {
        documentId,
        contextAnalysis: response?.data || null,
      };
    } catch (err) {
      return rejectWithValue({
        documentId,
        message:
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to refresh context analysis",
        status: err?.response?.status,
      });
    }
  }
);

/**
 * Shared merge logic for "patch one job into the cache in place"
 * reducers — the 5s status poll and the cancel action both need to
 * splice an updated job into `jobsByDocument` (and its `jobsByMatter`
 * mirror) without disturbing sibling jobs or already-known fields the
 * response omits. See `fetchAnalysisJobStatus.fulfilled`'s original
 * comment for the full rationale on the matter-cache patch.
 */
const _mergeJobIntoState = (state, { documentId, jobId, job }) => {
  if (!documentId || !jobId || !job) return;

  Object.values(state.jobsByMatter).forEach((matterSlot) => {
    const matterJobs = matterSlot?.jobs;
    if (!Array.isArray(matterJobs)) return;
    const matterIdx = matterJobs.findIndex(
      (j) => String(j?.id) === String(jobId)
    );
    if (matterIdx !== -1) {
      matterJobs[matterIdx] = { ...matterJobs[matterIdx], ...job };
    }
  });

  const slot = state.jobsByDocument[documentId];
  if (!slot) {
    state.jobsByDocument[documentId] = {
      status: "ready",
      jobs: [{ ...job }],
      error: null,
    };
    return;
  }
  const existingJobs = slot.jobs || [];
  const idx = existingJobs.findIndex((j) => String(j?.id) === String(jobId));
  if (idx === -1) {
    slot.jobs = [{ ...job }, ...existingJobs];
  } else {
    slot.jobs = existingJobs.map((j, i) => (i === idx ? { ...j, ...job } : j));
  }
  slot.status = "ready";
  slot.error = null;
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    /**
     * Drop all cached AI state for a single document. Used when a tab is
     * closed so a re-open re-fetches fresh data instead of showing stale
     * briefings.
     *
     * Also drops any cached reports for jobs belonging to this document
     * — otherwise a future re-open of the same doc would still hold the
     * pre-rerun report keyed on the now-defunct jobId, leaking memory
     * for any matter that's been worked on heavily in one session.
     *
     * IMPORTANT: `jobsByDocument[documentId].jobs` is a MATTER-WIDE list
     * (the hydration fetch queries the matter endpoint), so most of the
     * jobs in it belong to OTHER documents. Evicting reports for all of
     * them meant that closing any tab in the matter — including the
     * automatic close when the open-tab limit is reached — wiped the
     * Brief report the user was actively reading in the AI pane, which
     * then sat on a skeleton until a manual Retry. Only evict reports
     * for jobs whose own `document` is the doc being closed. Jobs with
     * no `document` field are the optimistic synthetic entries created
     * by `triggerAnalysisJob` on this same document, so they're evicted
     * too (preserves the original memory-cleanup intent).
     */
    clearDocumentAIState: (state, action) => {
      const documentId = action.payload;
      if (documentId == null) return;
      const jobsForDoc = state.jobsByDocument[documentId]?.jobs || [];
      jobsForDoc.forEach((j) => {
        if (j?.id == null) return;
        if (j?.document == null || String(j.document) === String(documentId)) {
          delete state.reportsByJob[j.id];
        }
      });
      delete state.briefings[documentId];
      delete state.jobsByDocument[documentId];
    },

    /**
     * Drop a single cached report. The full report is bulky (50KB–500KB
     * of markdown), so when a user closes the panel we don't want to
     * pile up reports for every job they've ever opened. Called from
     * AnalysisReport's unmount cleanup as a softer alternative to the
     * full per-document clear above.
     */
    clearReportForJob: (state, action) => {
      const jobId = action.payload;
      if (jobId == null) return;
      delete state.reportsByJob[jobId];
    },

    /**
     * Drop the cached context analysis for one document. Useful when
     * the user closes the inbox right rail and we want a fresh fetch
     * on next open instead of showing potentially stale content. We
     * don't auto-call this on inbox-page unmount because the same
     * doc may also be open in the matter workspace and that pane
     * relies on the cache being warm.
     */
    clearContextAnalysisForDocument: (state, action) => {
      const documentId = action.payload;
      if (documentId == null) return;
      delete state.contextAnalysisByDocument[documentId];
    },
  },
  extraReducers: (builder) => {
    builder
      // Briefing
      .addCase(fetchDocumentBriefing.pending, (state, action) => {
        const documentId = action.meta.arg;
        if (!documentId) return;
        state.briefings[documentId] = {
          status: "loading",
          data: state.briefings[documentId]?.data || null,
          error: null,
        };
      })
      .addCase(fetchDocumentBriefing.fulfilled, (state, action) => {
        const { documentId, briefing, empty } = action.payload || {};
        if (!documentId) return;
        state.briefings[documentId] = {
          status: empty ? "empty" : "ready",
          data: empty ? null : briefing,
          error: null,
        };
      })
      .addCase(fetchDocumentBriefing.rejected, (state, action) => {
        const documentId = action.meta.arg;
        if (!documentId) return;
        state.briefings[documentId] = {
          status: "failed",
          data: state.briefings[documentId]?.data || null,
          error: action.payload?.message || "Failed to load briefing",
        };
      })

      // Jobs list (mount-time hydration).
      //
      // The thunk arg is either a bare documentId (legacy / back-compat) or
      // an object `{ documentId, matterId }` (current callers). Normalize
      // here so the slice always keys `jobsByDocument` on the real document
      // ID — without this, dispatching the object shape would key state on
      // the literal string "[object Object]" and the UI would render an
      // eternal loading skeleton (pending) and never surface errors
      // (rejected). See triggerAnalysisJob.rejected below for the same
      // pattern.
      .addCase(fetchAnalysisJobs.pending, (state, action) => {
        const arg = action.meta.arg;
        const documentId = typeof arg === "object" && arg !== null ? arg.documentId : arg;
        const matterId = typeof arg === "object" && arg !== null ? arg.matterId : null;
        // documentId is optional for matter-scoped fetches (matter root,
        // no document open) — skip the per-doc slot, still warm the
        // matter cache below.
        if (documentId) {
          state.jobsByDocument[documentId] = {
            status: "loading",
            jobs: state.jobsByDocument[documentId]?.jobs || [],
            error: null,
          };
        }
        // Matter-cache mirror: do NOT downgrade an already-ready matter
        // cache to "loading" mid-flight. The matter cache exists to let
        // `selectAnalysisJobsForActiveDocument` serve the cross-doc
        // citation-jump landing instantly without flashing a skeleton.
        // The fallback selector requires `matterSlot.status === "ready"`,
        // so if we transition `ready → loading` here every per-document
        // backfill kicked off by `AnalysisTab.useEffect` would force
        // the UI through Brief → skeleton → Brief — exactly the
        // regression this PR is trying to kill. Bugbot caught this on
        // the first round of the fix. Only refresh the matter cache to
        // "loading" when it's empty (idle / failed / undefined). The
        // fulfilled handler refreshes the data either way.
        if (matterId != null) {
          const existing = state.jobsByMatter[matterId];
          if (!existing || existing.status !== "ready") {
            state.jobsByMatter[matterId] = {
              status: "loading",
              jobs: existing?.jobs || [],
              error: null,
            };
          }
        }
      })
      .addCase(fetchAnalysisJobs.fulfilled, (state, action) => {
        const { documentId, matterId, jobs } = action.payload || {};
        if (!documentId && !matterId) return;
        // Stale-response guard (Bugbot, PR #554, high): a jobs-list
        // request that started BEFORE a trigger POST finished returns a
        // list that doesn't contain the optimistic synthetic job yet.
        // Blindly replacing the slot would wipe the synthetic, snap the
        // matter-first pane away from the run the user just started,
        // and tear down its polling. Carry over cached synthetics the
        // response doesn't know about; the first response that DOES
        // contain the real job id naturally replaces them (they're no
        // longer "missing").
        const withSynthetics = (slot) => {
          const cached = slot?.jobs || [];
          const responseIds = new Set(jobs.map((j) => String(j?.id)));
          const missingSynthetics = cached.filter(
            (j) => j?.synthetic && !responseIds.has(String(j?.id))
          );
          return [...missingSynthetics, ...jobs];
        };
        if (documentId) {
          state.jobsByDocument[documentId] = {
            status: "ready",
            jobs: withSynthetics(state.jobsByDocument[documentId]),
            error: null,
          };
        }
        // When the fetch was matter-scoped, the response IS the matter-
        // wide jobs list (BE serves the same payload either way for the
        // matter-scoped endpoint), so keep both caches in sync. Sibling
        // documents in the same matter then get the cache-hit fallback
        // and don't flash a skeleton.
        if (matterId != null) {
          state.jobsByMatter[matterId] = {
            status: "ready",
            jobs: withSynthetics(state.jobsByMatter[matterId]),
            error: null,
          };
        }
        // Phase 3 will track the running job id here so the polling hook can
        // resume from a hydrated state. For Phase 1 the panel only needs to
        // know "is anything running?" via selectIsAnalysisRunningForDocument.
      })
      .addCase(fetchAnalysisJobs.rejected, (state, action) => {
        const arg = action.meta.arg;
        const documentId = typeof arg === "object" && arg !== null ? arg.documentId : arg;
        const matterId = typeof arg === "object" && arg !== null ? arg.matterId : null;
        if (documentId) {
          state.jobsByDocument[documentId] = {
            status: "failed",
            jobs: state.jobsByDocument[documentId]?.jobs || [],
            error: action.payload?.message || "Failed to load jobs",
          };
        }
        // Same logic as the pending handler: don't poison a hydrated
        // matter cache because a per-document backfill failed. The
        // matter cache reflects what the server told us about the
        // matter — a per-doc fetch failure (network blip, transient
        // 5xx) doesn't invalidate the matter-wide payload. Without
        // this guard, a flaky network on a cross-doc citation jump
        // would not only fail to backfill the per-doc slot but ALSO
        // erase the matter-cache content that was making the cross-
        // doc render skeleton-free.
        if (matterId != null) {
          const existing = state.jobsByMatter[matterId];
          if (!existing || existing.status !== "ready") {
            state.jobsByMatter[matterId] = {
              status: "failed",
              jobs: existing?.jobs || [],
              error: action.payload?.message || "Failed to load jobs",
            };
          }
        }
      })

      // Trigger.
      //
      // We deliberately don't write a `pending` reducer here. The
      // shared `jobsByDocument[documentId].status` field is consumed by
      // AnalysisTab to gate its initial-hydration skeleton; flipping it
      // to "loading" the moment the user clicks Run would unmount the
      // empty-state CTA (and its in-place button spinner) and replace
      // it with a jarring full-pane skeleton — even though we already
      // have hydrated jobs cached. AnalysisTab owns its own
      // `isTriggering` local state for the button spinner, so the
      // slice doesn't need to mirror that. The fulfilled handler still
      // does the meaningful work of optimistically prepending a
      // synthetic running job, which transitions the panel from the
      // empty-state CTA to the "Analyzing…" stub.
      .addCase(triggerAnalysisJob.fulfilled, (state, action) => {
        const { documentId, matterId, job } = action.payload || {};
        if (!documentId || !job?.job_id) return;
        const existing = state.jobsByDocument[documentId]?.jobs || [];
        // Optimistically prepend a synthetic running job so the panel
        // transitions empty → running before the next jobs-list poll
        // returns. Phase 3 will replace this with real polling state.
        // `document` is set so consumers that key polling / retry off
        // the job's owning document (matter-first AI pane) resolve to
        // the doc the run was triggered against. `synthetic` marks it
        // for the stale-response guard in fetchAnalysisJobs.fulfilled:
        // list responses that predate this POST must not evict it.
        // `status` echoes the response: a `queue: true` trigger returns
        // status="queued" (BE PR #1898) and the synthetic must carry it
        // so the pane renders the queued state — in BOTH caches, since
        // the same object is prepended to each below. The workflow
        // fields ride along so the queued message ("Queued — starts
        // automatically…") shows before the first poll lands.
        const synthetic = {
          id: job.job_id,
          document: documentId,
          status: job.status || "running",
          workflow_phase: job.workflow_phase,
          workflow_phase_message: job.workflow_phase_message,
          completed_conditions: 0,
          total_conditions: 0,
          created_at: new Date().toISOString(),
          synthetic: true,
        };
        state.jobsByDocument[documentId] = {
          status: "ready",
          jobs: [synthetic, ...existing],
          error: null,
        };
        // Mirror into the matter cache: the AI pane renders matter-first,
        // so the "Starting analysis…" transition has to come from here —
        // the matter cache is otherwise only refreshed by hydration.
        if (matterId != null) {
          const matterJobs = state.jobsByMatter[matterId]?.jobs || [];
          state.jobsByMatter[matterId] = {
            status: "ready",
            jobs: [synthetic, ...matterJobs],
            error: null,
          };
        }
      })
      .addCase(triggerAnalysisJob.rejected, (state, action) => {
        const documentId = action.meta.arg?.documentId;
        if (!documentId) return;
        state.jobsByDocument[documentId] = {
          status: "failed",
          jobs: state.jobsByDocument[documentId]?.jobs || [],
          error: action.payload?.message || "Failed to start analysis",
        };
      })

      // Status poll.
      //
      // We deliberately don't write a `pending` reducer either. The
      // poll fires every 5s; flipping the slot's `status` to
      // "loading" would either make the AnalysisProgressView
      // unmount in/out or force every component watching
      // `selectJobsForDocument` to re-render twice per tick. Real
      // failures are surfaced through the polling hook's local
      // retry-with-backoff logic; isolated transient errors don't
      // need to clear the cached progress data.
      .addCase(fetchAnalysisJobStatus.fulfilled, (state, action) => {
        // Patch the matter-wide cache too. While a job is running,
        // `selectAnalysisJobsForActiveDocument` deliberately prefers
        // `jobsByMatter` over the per-document slot, but that cache is
        // only ever written by mount-time hydration — which serves
        // `AnalysisJobListSerializer` and carries no `conditions[]`.
        // Patching only `jobsByDocument` therefore left the progress
        // view frozen on its mount-time snapshot: a counter stuck at
        // whatever was complete when the panel opened, and a checklist
        // that stayed empty for the life of the job.
        _mergeJobIntoState(state, action.payload || {});
      })
      .addCase(fetchAnalysisJobStatus.rejected, (state, action) => {
        // Don't blow away the cached progress on a single transient
        // poll failure. The polling hook handles retry/backoff —
        // the slice just records the last-poll error so the UI
        // can render a soft "reconnecting…" hint if needed.
        const { documentId, jobId } = action.meta.arg || {};
        if (!documentId || !jobId) return;
        const slot = state.jobsByDocument[documentId];
        if (!slot) return;
        slot.error = action.payload?.message || "Failed to poll job status";
      })

      // Cancel — the real "Cancel" button. Patches the job in place
      // immediately on success (same merge as the status poll) so the
      // UI reflects "cancelled" right away instead of waiting for the
      // next 5s poll tick. Failure (e.g. the job already finished by
      // the time the click landed) is surfaced by the component via a
      // toast — the slice doesn't need its own error slot for this.
      .addCase(cancelAnalysisJob.fulfilled, (state, action) => {
        _mergeJobIntoState(state, action.payload || {});
      })

      // Phase 4: full report.
      //
      // Cached per-job because reports are large and the user can
      // re-open the panel on the same job dozens of times in a
      // session — keying on jobId means we re-fetch only when the
      // user runs a fresh analysis (which produces a new jobId).
      .addCase(fetchAnalysisJobReport.pending, (state, action) => {
        const jobId = action.meta.arg;
        if (jobId == null) return;
        state.reportsByJob[jobId] = {
          status: "loading",
          data: state.reportsByJob[jobId]?.data || null,
          error: null,
        };
      })
      .addCase(fetchAnalysisJobReport.fulfilled, (state, action) => {
        const { jobId, report } = action.payload || {};
        if (jobId == null) return;
        state.reportsByJob[jobId] = {
          status: "ready",
          data: report,
          error: null,
        };
      })
      .addCase(fetchAnalysisJobReport.rejected, (state, action) => {
        const jobId = action.meta.arg;
        if (jobId == null) return;
        state.reportsByJob[jobId] = {
          status: "failed",
          // Preserve any previously-loaded report so a transient
          // failure (e.g. backend bounce) doesn't clear the markdown
          // the user is reading — they'll see the inline retry banner
          // but the content stays put.
          data: state.reportsByJob[jobId]?.data || null,
          error: action.payload?.message || "Failed to load report",
        };
      })

      // Phase 5: context analysis fetch.
      //
      // Mirrors the briefing pattern (per-document keyed cache,
      // preserves prior data on failure so the markdown the user is
      // reading doesn't disappear on a transient blip), with the
      // important twist that BE always returns 200 — including for
      // the "no row yet" case where `data.status === "pending"` and
      // `data.computed_at === null`. Polling is driven off
      // `data.status`, not `slice.status`, by `usePollContextAnalysis`.
      .addCase(fetchContextAnalysis.pending, (state, action) => {
        const documentId = action.meta.arg;
        if (!documentId) return;
        state.contextAnalysisByDocument[documentId] = {
          status: "loading",
          data: state.contextAnalysisByDocument[documentId]?.data || null,
          error: null,
        };
      })
      .addCase(fetchContextAnalysis.fulfilled, (state, action) => {
        const { documentId, contextAnalysis } = action.payload || {};
        if (!documentId) return;
        state.contextAnalysisByDocument[documentId] = {
          status: "ready",
          data: contextAnalysis,
          error: null,
        };
      })
      .addCase(fetchContextAnalysis.rejected, (state, action) => {
        const documentId = action.meta.arg;
        if (!documentId) return;
        state.contextAnalysisByDocument[documentId] = {
          status: "failed",
          data: state.contextAnalysisByDocument[documentId]?.data || null,
          error: action.payload?.message || "Failed to load context analysis",
        };
      })

      // Refresh — Phase 5b consumer; reducer is here so the lifecycle
      // is owned in one place. Optimistically transitions data.status
      // to "pending" on fulfilled so the polling hook resumes
      // immediately. Failure preserves prior data (same rationale as
      // the regular fetch's rejected reducer).
      .addCase(refreshContextAnalysis.fulfilled, (state, action) => {
        const { documentId, contextAnalysis } = action.payload || {};
        if (!documentId) return;
        const prior = state.contextAnalysisByDocument[documentId]?.data || null;
        state.contextAnalysisByDocument[documentId] = {
          status: "ready",
          data:
            contextAnalysis ||
            // BE returns the reset row on 202, but if the response is
            // empty for any reason, optimistically set status=pending
            // so polling kicks in.
            (prior
              ? { ...prior, status: "pending", error_message: "" }
              : {
                  status: "pending",
                  content_md: "",
                  recommended_actions: [],
                  computed_at: null,
                  model_version: "",
                  error_message: "",
                }),
          error: null,
        };
      })
      .addCase(refreshContextAnalysis.rejected, (state, action) => {
        const documentId = action.meta.arg;
        if (!documentId) return;
        const slot = state.contextAnalysisByDocument[documentId];
        if (!slot) return;
        slot.error = action.payload?.message || "Failed to refresh context analysis";
      });
  },
});

export const {
  clearDocumentAIState,
  clearReportForJob,
  clearContextAnalysisForDocument,
} = aiSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

// Stable references for the "no entry yet" case. Returning a fresh object
// literal on every selector call would force `useSelector` to treat it as
// a new value and re-render any component that subscribes to a doc that
// hasn't been hydrated yet (most documents on first paint). Module-scope
// frozen constants keep referential equality across calls.
const EMPTY_BRIEFING_STATE = Object.freeze({
  status: "idle",
  data: null,
  error: null,
});
const EMPTY_JOBS_STATE = Object.freeze({
  status: "idle",
  jobs: Object.freeze([]),
  error: null,
});

export const selectBriefingForDocument = (state, documentId) =>
  state.ai?.briefings?.[documentId] || EMPTY_BRIEFING_STATE;

export const selectJobsForDocument = (state, documentId) =>
  state.ai?.jobsByDocument?.[documentId] || EMPTY_JOBS_STATE;

export const selectJobsForMatter = (state, matterId) =>
  state.ai?.jobsByMatter?.[matterId] || EMPTY_JOBS_STATE;

/**
 * Cross-cache selector for AnalysisTab.
 *
 * MATTER-FIRST (Matthew, 2026-08-18: "that side window just needs to
 * stay static — nothing I do in the other windows can affect it").
 * The Brief is a matter-wide artifact, so when a matterId is present
 * the matter cache is authoritative and the active document is
 * irrelevant to what the pane renders. The pre-2026-08-18 order
 * preferred a per-document slot with content, which made the pane
 * REACT to document switches: opening an evidence document that had
 * its own older analysis job hijacked the pane away from the matter
 * Brief the user was reading.
 *
 * Returns the same `{ status, jobs, error }` shape as
 * `selectJobsForDocument` so AnalysisTab can drop it in without
 * other plumbing changes.
 *
 * Resolution order (matterId present):
 *   1. Matter slot `ready` → matter slot, with ONE exception: when
 *      the matter cache's newest job says `running` but the per-doc
 *      slot holds the SAME job in a terminal state, the per-doc slot
 *      wins — a doc-scoped refetch can learn about completion before
 *      the matter cache does, and pinning "running" would freeze the
 *      progress view on a finished job.
 *   2. Matter slot not ready, per-doc slot `ready` with content →
 *      per-doc slot (warm-cache fallback while the matter cache
 *      hydrates; avoids a skeleton flash on first open).
 *   3. Matter slot `failed` → matter slot (retry banner).
 *   4. Otherwise → whichever slot is further along (per-doc `failed`
 *      surfaces trigger errors; else the matter slot drives the
 *      skeleton while hydrating).
 *
 * Without a matterId (legacy/doc-only callers) the per-document slot
 * is returned unchanged.
 */
export const selectAnalysisJobsForActiveDocument = (state, documentId, matterId) => {
  const docSlot = selectJobsForDocument(state, documentId);
  if (matterId == null) return docSlot;

  const matterSlot = selectJobsForMatter(state, matterId);
  if (matterSlot.status === "ready") {
    const jobs = matterSlot.jobs || [];
    // "Active" includes queued (BE PR #1898): a queued Brief is the
    // pane's subject exactly like a running one. The freshness
    // exception only fires when the per-doc copy is TERMINAL — a doc
    // copy that merely says running-vs-queued is a promotion in
    // flight, not a completion, and the poll patches the matter cache
    // directly anyway.
    const newest = jobs.find((j) => isActiveBriefStatus(j?.status)) || jobs[0] || null;
    if (newest && isActiveBriefStatus(newest.status)) {
      const fresherDocCopy = (docSlot?.jobs || []).some(
        (j) =>
          String(j?.id) === String(newest.id) &&
          !isActiveBriefStatus(j?.status)
      );
      if (fresherDocCopy) return docSlot;
    }
    return matterSlot;
  }
  if (docSlot.status === "ready" && docSlot.jobs.length > 0) return docSlot;
  if (matterSlot.status === "failed") return matterSlot;
  if (docSlot.status === "failed") return docSlot;
  return matterSlot;
};

/**
 * Companion to `selectAnalysisJobsForActiveDocument` — picks the active
 * job to render in the same priority order. Used by AnalysisTab so a
 * cross-doc citation jump within the same matter shows the same job
 * (typically the latest completed Brief) instantly without re-fetching.
 */
export const selectActiveJobForActiveDocument = (state, documentId, matterId) => {
  const slot = selectAnalysisJobsForActiveDocument(state, documentId, matterId);
  const jobs = slot?.jobs || [];
  if (jobs.length === 0) return null;
  // Queued counts: a queued Brief is what the pane should render (and
  // poll — the poll is how the UI learns the backend promoted it).
  return jobs.find((j) => isActiveBriefStatus(j?.status)) || jobs[0] || null;
};

export const selectIsAnalysisRunningForDocument = (state, documentId) => {
  const jobs = state.ai?.jobsByDocument?.[documentId]?.jobs || [];
  // "Is a Brief active" — every consumer uses this to disable its
  // re-trigger control, and re-triggering over a queued job is just as
  // pointless as over a running one (the backend would return the same
  // queued job).
  return jobs.some((j) => isActiveBriefStatus(j?.status));
};

/**
 * Returns the job we should be displaying / polling for this document:
 *   1. Any running job (only one can run at a time — backend enforces
 *      via 409 on POST).
 *   2. Else the newest job in the cached list (Phase 1 hydration
 *      sorts newest-first).
 *   3. Else null.
 *
 * Returning the running job first keeps the polling hook simple — it
 * reads this selector, gets a job, and polls if `status` isn't terminal.
 */
export const selectActiveJobForDocument = (state, documentId) => {
  const jobs = state.ai?.jobsByDocument?.[documentId]?.jobs || [];
  if (jobs.length === 0) return null;
  // Queued counts as active — the polling hook must keep polling a
  // queued job so the view flips to running when the backend promotes.
  return jobs.find((j) => isActiveBriefStatus(j?.status)) || jobs[0] || null;
};

/**
 * Set of document IDs (as strings) that currently have a running analysis
 * job. Memoized via reselect's `createSelector` against the underlying
 * `jobsByDocument` reference so consumers in long lists (matter document
 * table, future inbox list) don't re-render every state tick. Returns a
 * Set so per-row lookups stay O(1) regardless of how many documents have
 * ever been polled.
 *
 * `createSelector` is reexported from `@reduxjs/toolkit` (bundled, no new
 * dep). Using it instead of a manual module-scope `let` cache so the
 * memoization keys on the input object identity — different stores
 * (Jest fixtures, future SSR, multi-store scenarios) hold different
 * `jobsByDocument` references and therefore cannot share a stale Set.
 * If `state.ai` is undefined we short-circuit to a frozen empty Set,
 * which never feeds the memo so the next real call still triggers a
 * recompute.
 */
// Same idle-default trick as the briefing/jobs selectors above —
// `useSelector` compares by reference, so returning a fresh object
// every call would re-render every component watching a not-yet-loaded
// report on every store update.
const EMPTY_REPORT_STATE = Object.freeze({
  status: "idle",
  data: null,
  error: null,
});

export const selectReportForJob = (state, jobId) =>
  state.ai?.reportsByJob?.[jobId] || EMPTY_REPORT_STATE;

const EMPTY_CONTEXT_ANALYSIS_STATE = Object.freeze({
  status: "idle",
  data: null,
  error: null,
});

export const selectContextAnalysisForDocument = (state, documentId) =>
  state.ai?.contextAnalysisByDocument?.[documentId] ||
  EMPTY_CONTEXT_ANALYSIS_STATE;

/**
 * Returns true if a producer run is actually in flight (`pending` or
 * `running`). The inbox row pulse animation and the right rail's
 * "running" pill consume this, and the panel disables its refresh /
 * start control on it — so it must mean "in flight", not merely
 * "non-terminal". `not_started` is non-terminal but nothing is running,
 * and reporting it as running disabled the only control that could
 * start an analysis.
 *
 * Polling is driven separately by `isTerminalContextAnalysisStatus`, so
 * a `not_started` document is still polled.
 */
export const selectIsContextAnalysisRunning = (state, documentId) => {
  const data = state.ai?.contextAnalysisByDocument?.[documentId]?.data;
  if (!data) return false;
  return isInFlightContextAnalysisStatus(data.status);
};

const FROZEN_EMPTY_DOC_ID_SET = Object.freeze(new Set());

export const selectRunningAnalysisDocIds = createSelector(
  [(state) => state.ai?.jobsByDocument],
  (jobsByDocument) => {
    if (!jobsByDocument) return FROZEN_EMPTY_DOC_ID_SET;
    const next = new Set();
    Object.keys(jobsByDocument).forEach((docId) => {
      const jobs = jobsByDocument[docId]?.jobs || [];
      // Queued counts: this Set feeds the matter table's "Run AI
      // Analysis" row action, which must be disabled while a Brief is
      // queued for the doc (re-triggering would return the same job).
      if (jobs.some((j) => isActiveBriefStatus(j?.status))) {
        next.add(String(docId));
      }
    });
    return next;
  }
);

export default aiSlice.reducer;
